/**
 * WP-06A/WP-06B — Loop lifecycle server command (port-injected, transport-free).
 *
 * This is the trusted server command layered over the database function
 * `public.apply_loop_lifecycle_command(...)`, applied to the hosted database in
 * WP-06B. It stays transport-free and database-free: it talks to an injected
 * `LoopLifecyclePort`, never to a Supabase client. The hosted implementation of
 * that port is `./lifecycleAdapter.server.ts`, reached only through the
 * authenticated server function in `./lifecycle.functions.ts`.
 *
 * Authority rules encoded here (mirroring the SQL — defence in depth, never a
 * substitute for it):
 *   * The actor is the VERIFIED session user id. It is never read from a request
 *     body, query string, header, form field, cookie, or localStorage value.
 *   * The caller cannot supply a workspace id, actor role, membership status,
 *     account state, `record_version`, `resolved_at`, `archived_at`, or the
 *     resulting status of a resolve/archive. The backend derives all of them.
 *   * `resolved` and `archived` are unreachable through `transitionStatus`;
 *     they have dedicated governed operations.
 *   * The UNTRUSTED caller cannot supply a raw idempotency key, a hash, or a
 *     request identity: the transport validator drops those keys and the trusted
 *     server boundary constructs the identity itself. The request key is derived
 *     server-side and hashed with SHA-256, so only the digest is ever sent
 *     onward (see ./requestIdentity for the full contract).
 *   * Every failure is classified into the stable, non-sensitive vocabulary in
 *     `./errors`. No provider message or raw exception crosses this boundary.
 *
 * PRODUCT CUTOVER IS NOT WP-06B: no product route, loader, or component calls
 * the lifecycle server function yet, and the existing localStorage prototype
 * store is deliberately left untouched. That migration is WP-06C.
 */

import {
  classifyLifecycleError,
  isRetryableLifecycleReason,
  type LoopLifecycleFailureReason,
} from "./errors";
import type {
  LoopLifecycleOperation,
  LoopLifecyclePort,
  LoopLifecycleRecord,
  LoopStatus,
  VerifiedActor,
} from "./lifecyclePort";
import {
  canonicalPayload,
  DETERMINISTIC_REQUEST_IDENTITY,
  isValidRequestIdentity,
  LIFECYCLE_OPERATION,
  resolveRequestKey,
  type LifecycleRequestIdentity,
} from "./requestIdentity";

export { LIFECYCLE_OPERATION };
export type { LifecycleRequestIdentity };

/** Upper bound enforced by the proposed `loops_status_reason_length_check`. */
export const STATUS_REASON_MAX_LENGTH = 500;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Statuses a client may request through `transitionStatus`. */
const REQUESTABLE_STATUSES: ReadonlySet<string> = new Set([
  "open",
  "waiting",
  "action_needed",
  "blocked",
  "ready_to_verify",
]);

/** Statuses that require a governed reason. Mirrors the SQL CHECK. */
const REASON_REQUIRED: ReadonlySet<string> = new Set(["waiting", "blocked"]);

/**
 * The transition matrix, duplicated from the SQL so the client layer can
 * disable impossible affordances. It is NOT the authority: the database refuses
 * an invalid transition even when this table is wrong or bypassed.
 */
const TRANSITIONS: Readonly<Record<LoopStatus, readonly LoopStatus[]>> = {
  open: ["waiting", "action_needed", "blocked", "ready_to_verify", "archived"],
  waiting: ["open", "action_needed", "blocked", "ready_to_verify", "archived"],
  action_needed: ["open", "waiting", "blocked", "ready_to_verify", "archived"],
  blocked: ["open", "waiting", "action_needed", "ready_to_verify", "archived"],
  ready_to_verify: ["open", "action_needed", "blocked", "resolved", "archived"],
  resolved: ["archived"],
  archived: [],
};

export function isTransitionAllowed(from: LoopStatus, to: LoopStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

export function allowedTransitions(from: LoopStatus): readonly LoopStatus[] {
  return TRANSITIONS[from];
}

export type LoopLifecycleFailure = {
  readonly ok: false;
  readonly reason: LoopLifecycleFailureReason;
  readonly retryable: boolean;
};

export type LoopLifecycleSuccess = { readonly ok: true; readonly record: LoopLifecycleRecord };

export type LoopLifecycleOutcome = LoopLifecycleSuccess | LoopLifecycleFailure;

const failure = (reason: LoopLifecycleFailureReason): LoopLifecycleFailure => ({
  ok: false,
  reason,
  retryable: isRetryableLifecycleReason(reason),
});

/** The client-supplied part of a lifecycle request. Intentionally minimal. */
export type LoopLifecycleRequest =
  | { readonly kind: "transitionStatus"; readonly targetStatus: string; readonly reason?: string }
  | {
      readonly kind: "verifyCondition";
      readonly conditionId: string;
      readonly method: "evidence" | "human_attestation";
      readonly evidenceId?: string;
    }
  | { readonly kind: "resolve" }
  | { readonly kind: "archive" }
  | { readonly kind: "snooze"; readonly snoozedUntil: string }
  | { readonly kind: "unsnooze" }
  /**
   * WP-06R: explicit reopen of a `resolved` Loop into a new resolution cycle.
   * Only the backend decides eligibility (`resolved` only), increments the
   * cycle, and resets current-cycle condition state. The optional reason is
   * bounded like a status reason and never required.
   */
  | { readonly kind: "reopen"; readonly reason?: string };

const OPERATIONS: Readonly<Record<LoopLifecycleRequest["kind"], LoopLifecycleOperation>> = {
  transitionStatus: "transition_status",
  verifyCondition: "verify_condition",
  resolve: "resolve",
  archive: "archive",
  snooze: "snooze",
  unsnooze: "unsnooze",
  reopen: "reopen",
};

/**
 * WP-06R client-side affordance hint only. `resolved` is not added to the
 * transition matrix: `transition_status` still cannot reach `action_needed`
 * from `resolved`; the dedicated `reopen` operation is the only edge.
 */
export function canReopen(status: LoopStatus): boolean {
  return status === "resolved";
}

export function isValidActorId(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

export function isValidRecordVersion(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1;
}

function sha256Hex(input: string): Promise<string> {
  return globalThis.crypto.subtle
    .digest("SHA-256", new TextEncoder().encode(input))
    .then((digest) =>
      Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join(""),
    );
}

/**
 * Take the SHA-256 digest of the resolved request key, SERVER-SIDE, and return
 * only the digest. See ./requestIdentity for where the key originates and why a
 * deterministic identity makes an automatic retry replay rather than reapply.
 * The raw key never leaves this module.
 */
export async function deriveLifecycleRequestKeyHash(
  identity: LifecycleRequestIdentity,
  actorUserId: string,
  loopId: string,
  operation: LoopLifecycleOperation,
  expectedVersion: number,
  payload: Readonly<Record<string, string>>,
): Promise<string> {
  return sha256Hex(
    resolveRequestKey(identity, actorUserId, loopId, operation, expectedVersion, payload),
  );
}

/**
 * The mutation fingerprint. Deliberately EXCLUDES the actor so that two
 * different users cannot silently share one idempotency record, and includes
 * every governed payload field so that reusing a key for a different mutation
 * is detectable rather than merely unlikely.
 */
export async function deriveLifecycleFingerprint(
  loopId: string,
  operation: LoopLifecycleOperation,
  expectedVersion: number,
  payload: Readonly<Record<string, string>>,
): Promise<string> {
  return sha256Hex(
    `${LIFECYCLE_OPERATION}#fingerprint:${loopId}:${operation}:${expectedVersion}:${canonicalPayload(payload)}`,
  );
}

/**
 * Validate the client request and reduce it to the exact governed payload the
 * authority accepts. Returns a failure reason instead of a payload when the
 * request is unusable, so nothing invalid ever reaches the port.
 */
export function buildLifecyclePayload(
  request: LoopLifecycleRequest,
): { ok: true; payload: Record<string, string> } | LoopLifecycleFailure {
  switch (request.kind) {
    case "transitionStatus": {
      const target = request.targetStatus;
      // `resolved` and `archived` are refused here, not silently redirected:
      // each has a dedicated operation with its own requirements.
      if (!REQUESTABLE_STATUSES.has(target)) return failure("validation_failed");
      const reason = typeof request.reason === "string" ? request.reason.trim() : "";
      if (REASON_REQUIRED.has(target) && reason.length === 0) return failure("validation_failed");
      if (reason.length > STATUS_REASON_MAX_LENGTH) return failure("validation_failed");
      const payload: Record<string, string> = { target_status: target };
      if (reason.length > 0) payload["reason"] = reason;
      return { ok: true, payload };
    }
    case "verifyCondition": {
      if (!isValidActorId(request.conditionId)) return failure("validation_failed");
      if (request.method === "evidence") {
        if (!isValidActorId(request.evidenceId)) return failure("validation_failed");
        return {
          ok: true,
          payload: {
            condition_id: request.conditionId,
            method: "evidence",
            evidence_id: request.evidenceId,
          },
        };
      }
      // An explicit human attestation must not carry evidence: the two methods
      // stay distinguishable in the audit record.
      if (request.evidenceId !== undefined) return failure("validation_failed");
      return {
        ok: true,
        payload: { condition_id: request.conditionId, method: "human_attestation" },
      };
    }
    case "snooze": {
      const parsed = Date.parse(request.snoozedUntil);
      if (Number.isNaN(parsed)) return failure("validation_failed");
      if (parsed <= Date.now()) return failure("validation_failed");
      return { ok: true, payload: { snoozed_until: new Date(parsed).toISOString() } };
    }
    case "reopen": {
      const reason = typeof request.reason === "string" ? request.reason.trim() : "";
      if (reason.length > STATUS_REASON_MAX_LENGTH) return failure("validation_failed");
      return { ok: true, payload: reason.length > 0 ? { reason } : {} };
    }
    case "resolve":
    case "archive":
    case "unsnooze":
      return { ok: true, payload: {} };
  }
}

/**
 * Execute one governed lifecycle command.
 *
 * The backend remains the sole authority: this function never computes a
 * resulting status, never increments a version, never sets a timestamp, and
 * never decides whether resolution requirements are met. It validates, resolves
 * the trusted request identity, hashes it, calls the authority once, and
 * classifies the outcome.
 *
 * `identity` is a TRUSTED SERVER-SIDE value (see ./requestIdentity). It defaults
 * to the deterministic identity, so a retried identical mutation replays. It is
 * never populated from untrusted request data.
 */
export async function applyLoopLifecycleCommand(
  port: LoopLifecyclePort,
  actor: VerifiedActor,
  loopId: string,
  expectedVersion: number,
  request: LoopLifecycleRequest,
  identity: LifecycleRequestIdentity = DETERMINISTIC_REQUEST_IDENTITY,
): Promise<LoopLifecycleOutcome> {
  if (!isValidActorId(actor?.userId)) return failure("validation_failed");
  if (!isValidActorId(loopId)) return failure("validation_failed");
  if (!isValidRecordVersion(expectedVersion)) return failure("validation_failed");
  if (!isValidRequestIdentity(identity)) return failure("validation_failed");

  const built = buildLifecyclePayload(request);
  if (built.ok !== true) return built;

  const operation = OPERATIONS[request.kind];
  const payload = built.payload;

  let requestKeyHash: string;
  let requestFingerprint: string;
  try {
    requestKeyHash = await deriveLifecycleRequestKeyHash(
      identity,
      actor.userId,
      loopId,
      operation,
      expectedVersion,
      payload,
    );

    requestFingerprint = await deriveLifecycleFingerprint(
      loopId,
      operation,
      expectedVersion,
      payload,
    );
  } catch {
    // A runtime without WebCrypto cannot derive a key, and guessing one would
    // defeat idempotency entirely.
    return failure("unavailable");
  }

  try {
    const record = await port.applyLifecycleCommand({
      actorUserId: actor.userId,
      loopId,
      expectedVersion,
      operation,
      requestKeyHash,
      requestFingerprint,
      payload,
    });
    if (!isUsableRecord(record)) return failure("unavailable");
    return { ok: true, record };
  } catch (error) {
    return failure(classifyLifecycleError(error));
  }
}

/**
 * A response that does not carry the authoritative fields is treated as an
 * outage rather than trusted. Silently accepting a malformed row would let a
 * broken authority masquerade as a successful mutation.
 */
function isUsableRecord(record: unknown): record is LoopLifecycleRecord {
  if (record === null || typeof record !== "object") return false;
  const candidate = record as Partial<LoopLifecycleRecord>;
  return (
    isValidActorId(candidate.loopId) &&
    isValidActorId(candidate.workspaceId) &&
    typeof candidate.status === "string" &&
    candidate.status in TRANSITIONS &&
    isValidRecordVersion(candidate.recordVersion) &&
    typeof candidate.replayed === "boolean"
  );
}
